// The notification-email contract shared by every submission surface that
// collects one — `SubmitTranslationModal` for a routine submit and
// `RollbackModal` for a rollback.
//
// It lives in one module rather than once per modal because both paths feed the
// SAME `SubmitStringGroupTranslation` mutation and must agree on what a valid
// recipient is: a regex or an error sentence that drifted between them would
// accept an address on one path and reject it on the other.

/** Well-formed address: non-space/@ local part, an `@`, a dotted domain. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Beyond being a well-formed address, a notification email must belong to an
// Amazon domain: `@amazon.` followed by an alphabetic-only TLD (e.g.
// amazon.com, amazon.dev). Disallowing anything but letters after `amazon.`
// also blocks look-alike domains such as `amazon.com.attacker.com`.
// Case-insensitive because email domains are case-insensitive.
export const AMAZON_EMAIL_REGEX = /^[^\s@]+@amazon\.[a-z]+$/i;

/** Resource IDS rather than resolved copy, so this stays a pure predicate module: it decides
 * which rule tripped, and the calling component resolves it. */
export const INVALID_EMAIL_ERROR_KEY = 'sgd-notification-email-invalid';
export const NON_AMAZON_EMAIL_ERROR_KEY = 'sgd-notification-email-non-amazon';

// Not a resource: an example address is identifier-shaped and reads the same in every locale.
export const NOTIFICATION_EMAIL_PLACEHOLDER = 'you@amazon.com';

/**
 * Resource id of the error to show beneath the notification-email field, or `undefined` when the
 * address is acceptable. The two ids are distinct so the user knows which rule they tripped.
 *
 * The field is REQUIRED on every surface that renders it, so a blank
 * (which fails {@link EMAIL_REGEX}) is reported as invalid rather than exempt.
 */
export function notificationEmailErrorKey(
  trimmedEmail: string,
): string | undefined {
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return INVALID_EMAIL_ERROR_KEY;
  }
  if (!AMAZON_EMAIL_REGEX.test(trimmedEmail)) {
    return NON_AMAZON_EMAIL_ERROR_KEY;
  }
  return undefined;
}
